import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Calendar,
  CheckCircle2,
  Loader2,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import Select from "react-select";
import {
  createCashRecord,
  getCashLookupAdvances,
  getCashLookupPayments,
} from "../../../api/finance/incomeExpenditure";
import {
  showConfirmDialog,
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import customSelectStyles from "../../../components/ui/customSelectStyles";

const fieldSelectStyles = {
  ...customSelectStyles,
  control: (provided, state) => ({
    ...customSelectStyles.control(provided, state),
    minHeight: "42px",
    borderRadius: "12px",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const RecordCashModal = ({ isOpen, onClose, direction, onSaved }) => {
  const isIn = direction === "in";
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;

  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [advanceOptions, setAdvanceOptions] = useState([]);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [outLinkType, setOutLinkType] = useState("payment");
  const [selectedAdvance, setSelectedAdvance] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [attachedFile, setAttachedFile] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [form, setForm] = useState({
    record_date: new Date().toISOString().slice(0, 10),
    amount: "",
    description: "",
    note: "",
  });

  const title = isIn ? "Ghi nhận thu" : "Ghi nhận chi";
  const Icon = isIn ? ArrowDownCircle : ArrowUpCircle;

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      record_date: new Date().toISOString().slice(0, 10),
      amount: "",
      description: "",
      note: "",
    });
    setSelectedAdvance(null);
    setSelectedPayment(null);
    setOutLinkType("payment");
    setAttachedFile(null);
    setPreviewName("");
    setOptionsLoaded(false);

    const load = async () => {
      setLoadingOptions(true);
      try {
        if (isIn) {
          const res = await getCashLookupAdvances("in");
          setAdvanceOptions(
            (res?.records || []).map((r) => ({
              value: r.id,
              label: `${r.request_code} — còn ${Number(r.remaining_amount || 0).toLocaleString("vi-VN")} ₫`,
              data: r,
            }))
          );
          setPaymentOptions([]);
        } else {
          const [advRes, payRes] = await Promise.all([
            getCashLookupAdvances("out"),
            getCashLookupPayments(),
          ]);
          setAdvanceOptions(
            (advRes?.records || []).map((r) => ({
              value: r.id,
              label: `${r.request_code} — ${Number(r.amount || 0).toLocaleString("vi-VN")} ₫`,
              data: r,
            }))
          );
          setPaymentOptions(
            (payRes?.records || []).map((r) => ({
              value: r.id,
              label: `${r.request_code} — ${Number(r.amount || 0).toLocaleString("vi-VN")} ₫`,
              data: r,
            }))
          );
        }
        setOptionsLoaded(true);
      } catch (error) {
        console.error("Load lookup failed:", error);
        const status = error?.response?.status;
        const serverMsg = error?.response?.data?.message;
        if (status === 403) {
          showErrorToast(serverMsg || "Bạn không có quyền xem hồ sơ đề nghị.");
        } else if (status === 404) {
          showErrorToast(
            "API chưa sẵn sàng. Vui lòng khởi động lại backend (npm start) rồi thử lại."
          );
        } else {
          showErrorToast(serverMsg || "Không thể tải hồ sơ đã duyệt.");
        }
      } finally {
        setLoadingOptions(false);
      }
    };
    load();
  }, [isOpen, isIn]);

  const activeDossier = useMemo(() => {
    if (isIn) return selectedAdvance?.data;
    if (outLinkType === "payment") return selectedPayment?.data;
    return selectedAdvance?.data;
  }, [isIn, outLinkType, selectedAdvance, selectedPayment]);

  const emptyLookupHint = useMemo(() => {
    if (!optionsLoaded || loadingOptions) return null;
    if (isIn) {
      return advanceOptions.length === 0
        ? "Chưa có đề nghị tạm ứng đã duyệt (hoặc đã ghi nhận đủ thu). Hãy duyệt ĐN tạm ứng tại module Tạm ứng trước."
        : null;
    }
    if (outLinkType === "payment") {
      return paymentOptions.length === 0
        ? "Chưa có đề nghị thanh toán đã duyệt chưa ghi nhận chi."
        : null;
    }
    return advanceOptions.length === 0
      ? "Chưa có đề nghị tạm ứng đã duyệt chưa ghi nhận chi."
      : null;
  }, [
    optionsLoaded,
    loadingOptions,
    isIn,
    outLinkType,
    advanceOptions.length,
    paymentOptions.length,
  ]);

  useEffect(() => {
    if (!activeDossier) return;
    const suggested = isIn
      ? activeDossier.remaining_amount
      : activeDossier.amount;
    if (suggested != null) {
      setForm((prev) => ({ ...prev, amount: String(suggested) }));
    }
    if (!isIn && activeDossier.payment_reason) {
      setForm((prev) => ({
        ...prev,
        description: activeDossier.payment_reason,
      }));
    }
  }, [activeDossier, isIn]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      showErrorToast("Chỉ cho phép file PDF.");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      showErrorToast("File vượt quá 25MB.");
      return;
    }
    setAttachedFile(file);
    setPreviewName(file.name);
  };

  const handleCancel = async () => {
    if (saving) return;
    const confirm = await showConfirmDialog({
      title: "Xác nhận hủy",
      text: "Bạn có muốn xóa dữ liệu đang nhập?",
      confirmText: "Xóa",
      cancelText: "Giữ lại",
    });
    if (confirm.isConfirmed) onClose?.();
  };

  const handleSubmit = async () => {
    const linkedAdvanceId = isIn
      ? selectedAdvance?.value
      : outLinkType === "advance"
        ? selectedAdvance?.value
        : null;
    const linkedPaymentId =
      !isIn && outLinkType === "payment" ? selectedPayment?.value : null;

    if (!linkedAdvanceId && !linkedPaymentId) {
      return showErrorToast("Vui lòng chọn hồ sơ đề nghị đã duyệt.");
    }
    if (!Number(form.amount) || Number(form.amount) <= 0) {
      return showErrorToast("Số tiền phải lớn hơn 0.");
    }

    const fd = new FormData();
    fd.append("direction", direction);
    fd.append("record_date", form.record_date);
    fd.append("amount", String(form.amount));
    if (form.description?.trim()) fd.append("description", form.description.trim());
    if (form.note?.trim()) fd.append("note", form.note.trim());
    if (linkedAdvanceId) fd.append("linked_advance_id", linkedAdvanceId);
    if (linkedPaymentId) fd.append("linked_payment_id", linkedPaymentId);
    if (attachedFile) fd.append("attachment", attachedFile);

    try {
      setSaving(true);
      await createCashRecord(fd);
      showSuccessToast(`${title} thành công.`);
      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("Record cash failed:", error);
      showErrorToast(
        error?.response?.data?.message || "Không thể ghi nhận thu-chi."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[50]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-[51] p-4"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl border border-slate-100 max-h-[92vh] overflow-hidden flex flex-col">
              <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <span
                    className={`p-2.5 rounded-2xl text-white shadow-lg ${
                      isIn ? "bg-emerald-600 shadow-emerald-200" : "bg-red-600 shadow-red-200"
                    }`}
                  >
                    <Icon size={20} />
                  </span>
                  <div>
                    <h2 className="text-xl font-black text-slate-800">{title}</h2>
                    <p className="text-sm text-slate-500 mt-0.5">
                      Gắn với hồ sơ đề nghị đã duyệt — không tạo đề nghị mới
                    </p>
                  </div>
                </div>
                <button type="button" onClick={onClose} className="text-slate-400 hover:text-red-600">
                  <X size={20} />
                </button>
              </div>

              <div className="px-6 py-5 overflow-y-auto space-y-4 flex-1">
                {emptyLookupHint ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    {emptyLookupHint}
                  </div>
                ) : null}

                {!isIn && (
                  <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
                    <button
                      type="button"
                      onClick={() => {
                        setOutLinkType("payment");
                        setSelectedAdvance(null);
                      }}
                      className={`flex-1 py-2 rounded-lg text-xs font-bold ${
                        outLinkType === "payment"
                          ? "bg-white text-red-700 shadow-sm"
                          : "text-slate-500"
                      }`}
                    >
                      ĐN thanh toán
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setOutLinkType("advance");
                        setSelectedPayment(null);
                      }}
                      className={`flex-1 py-2 rounded-lg text-xs font-bold ${
                        outLinkType === "advance"
                          ? "bg-white text-red-700 shadow-sm"
                          : "text-slate-500"
                      }`}
                    >
                      ĐN tạm ứng (chi)
                    </button>
                  </div>
                )}

                {(isIn || outLinkType === "advance") && (
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                      Hồ sơ tạm ứng đã duyệt *
                    </label>
                    <Select
                      value={selectedAdvance}
                      onChange={setSelectedAdvance}
                      options={advanceOptions}
                      styles={fieldSelectStyles}
                      menuPortalTarget={document.body}
                      isLoading={loadingOptions}
                      placeholder="Chọn đề nghị tạm ứng..."
                      isClearable
                    />
                  </div>
                )}

                {!isIn && outLinkType === "payment" && (
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                      Hồ sơ thanh toán đã duyệt *
                    </label>
                    <Select
                      value={selectedPayment}
                      onChange={setSelectedPayment}
                      options={paymentOptions}
                      styles={fieldSelectStyles}
                      menuPortalTarget={document.body}
                      isLoading={loadingOptions}
                      placeholder="Chọn đề nghị thanh toán..."
                      isClearable
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                      Ngày ghi nhận *
                    </label>
                    <div className="relative">
                      <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="date"
                        value={form.record_date}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, record_date: e.target.value }))
                        }
                        className="w-full h-[42px] pl-10 pr-3 rounded-xl border border-slate-200 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                      Số tiền *
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={form.amount}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, amount: e.target.value }))
                      }
                      className="w-full h-[42px] px-3 rounded-xl border border-slate-200 text-sm font-bold text-red-700"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                    Diễn giải
                  </label>
                  <textarea
                    rows={2}
                    value={form.description}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, description: e.target.value }))
                    }
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm resize-none"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                    Ghi chú nội bộ
                  </label>
                  <textarea
                    rows={2}
                    value={form.note}
                    onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm resize-none"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                    Chứng từ đính kèm (PDF)
                  </label>
                  <div className="flex items-center gap-2 h-[42px] rounded-xl border border-slate-200 bg-white px-3">
                    <Upload size={16} className="text-slate-400 shrink-0" />
                    <label htmlFor="cashRecordFile" className="flex-1 min-w-0 cursor-pointer">
                      <span className="block text-sm text-slate-600 truncate">
                        {previewName || "Chọn file PDF (tối đa 25MB)"}
                      </span>
                      <input
                        type="file"
                        id="cashRecordFile"
                        className="hidden"
                        accept=".pdf"
                        onChange={handleFileSelect}
                      />
                    </label>
                    {attachedFile ? (
                      <button
                        type="button"
                        onClick={() => {
                          setAttachedFile(null);
                          setPreviewName("");
                        }}
                        className="text-slate-400 hover:text-red-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    ) : null}
                  </div>
                  {attachedFile ? (
                    <p className="text-[11px] text-slate-400 mt-1">
                      {formatFileSize(attachedFile.size)} · {user?.name || "—"}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="px-6 py-4 border-t border-slate-100 flex justify-between gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-5 py-2.5 font-bold text-slate-500"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving}
                  className={`px-6 py-2.5 rounded-xl text-white font-bold flex items-center gap-2 disabled:opacity-70 ${
                    isIn ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  {saving ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <CheckCircle2 size={16} />
                  )}
                  {title}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default RecordCashModal;
